import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useRouterState,
  isRedirect,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Home, LogOut, Map, Search, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { AngaMark } from "@/components/brand/AngaMark";
import { ApiError, authApi, getToken } from "@/lib/api";
import { liveApi } from "@/lib/api/live";
import { usePlatformConfig } from "@/lib/config";
import { useRealtime } from "@/lib/realtime/useRealtime";
import { isDemoMode } from "@/lib/demo";
import { needsGooglePhoneGate } from "@/lib/phoneGate";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app")({
  beforeLoad: async ({ location }) => {
    if (typeof window === "undefined") return;
    const path = location.pathname;
    const onLocationGate = path.includes("/location-gate");
    const onPhoneGate = path.includes("/phone-gate");
    const demo = isDemoMode();
    const token = getToken("user");

    if (!token && !demo) {
      throw redirect({ to: "/login" });
    }
    if (!token && demo) return;
    if (onLocationGate) return;

    try {
      const me = await authApi.me();
      const needsPhone = needsGooglePhoneGate(me.user);

      if (needsPhone) {
        if (me.onboardingComplete) {
          if (!onPhoneGate) throw redirect({ to: "/app/phone-gate" });
          return;
        }
        if (me.dashboard?.startsWith("/onboarding")) {
          throw redirect({ href: me.dashboard });
        }
      }

      if (onPhoneGate && !needsPhone) {
        throw redirect({ to: "/app/home" });
      }

      if (!me.onboardingComplete && me.dashboard?.startsWith("/onboarding")) {
        // Only redirect to onboarding if this is not already an in-app navigation
        // (i.e. the user explicitly landed on /app, not navigating between tabs).
        // The profile completion modal on Home handles incomplete profiles for
        // users already inside the app.
        const comingFromLogin = typeof document !== "undefined" &&
          (document.referrer.includes("/login") || document.referrer.includes("/onboarding") || document.referrer === "");
        if (comingFromLogin) {
          throw redirect({ href: me.dashboard });
        }
      }
    } catch (err) {
      if (isRedirect(err)) throw err;
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: "/login" });
      }
    }
  },
  component: AppLayout,
});

function AppLayout() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { platformName } = usePlatformConfig();
  const { connected, lastTick } = useRealtime("user", true);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const demo = isDemoMode();
  const token = typeof window !== "undefined" ? getToken("user") : null;

  const [offline, setOffline] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false,
  );

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    if (!token && !demo) {
      void navigate({ to: "/login" });
    }
  }, [demo, navigate, token]);

  const isGate =
    pathname.includes("/location-gate") || pathname.includes("/phone-gate");

  const activeTab = pathname.includes("/map")
    ? "map"
    : pathname.includes("/alerts")
      ? "alerts"
      : pathname.includes("/search") || pathname.includes("/prefs")
        ? "search"
        : "home";

  const alertsQuery = useQuery({
    queryKey: ["user", "alerts"],
    queryFn: () => liveApi.userAlerts(),
    retry: 1,
    enabled: !isGate,
    refetchInterval: connected ? false : 30_000,
    staleTime: 0,
  });
  const alertCount = Number(alertsQuery.data?.alerts?.length ?? 0);

  const headerSubtitle =
    activeTab === "search"
      ? "Search"
      : activeTab === "map"
        ? "Map"
        : activeTab === "alerts"
          ? `Alerts${alertCount > 0 ? ` · ${alertCount}` : ""}`
          : "Home";

  async function logout() {
    const { confirmed } = await confirmAction({
      title: "Sign out?",
      description: "You will leave your resident dashboard.",
      confirmLabel: "Sign out",
      destructive: true,
    });
    if (!confirmed) return;
    await authApi.logoutUser();
    toast.success("Signed out");
    window.location.assign("/login");
  }

  if (isGate) {
    return <Outlet />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* Offline banner */}
      {offline ? (
        <div className="bg-[#064E3B] px-4 py-2 text-center text-xs font-semibold text-[#F8FAFC]">
          Offline — showing last updated data
        </div>
      ) : null}

      {/* Header */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#064E3B] text-white">
          <AngaMark className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-base font-bold">{platformName}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {headerSubtitle}
            {connected ? " · Live" : ""}
            {lastTick ? ` · ${new Date(String(lastTick)).toLocaleTimeString()}` : ""}
          </p>
        </div>
        <Link
          to="/app/settings"
          aria-label="Settings"
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Settings className="h-4 w-4" />
        </Link>
        <Button
          size="icon"
          variant="ghost"
          aria-label="Log out"
          onClick={() => void logout()}
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </header>

      {/* Page content — full width, padded, bottom-safe */}
      <main className="mx-auto w-full max-w-lg flex-1 space-y-5 px-4 py-5 pb-24">
        <Outlet />
      </main>

      {/* Bottom navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-background/95 backdrop-blur"
        aria-label="Main navigation"
      >
        <div className="mx-auto grid max-w-lg grid-cols-4 gap-1 px-2 py-2">
          <NavItem
            icon={Home}
            label={t("prefs.navHome")}
            href="/app/home"
            active={activeTab === "home"}
          />
          <NavItem
            icon={Map}
            label={t("prefs.navMap")}
            href="/app/map"
            active={activeTab === "map"}
          />
          <NavItem
            icon={AlertTriangle}
            label={t("prefs.navAlerts")}
            href="/app/alerts"
            active={activeTab === "alerts"}
            badge={alertCount > 0 ? alertCount : undefined}
          />
          <NavItem
            icon={Search}
            label="Search"
            href="/app/search"
            active={activeTab === "search"}
          />
        </div>
      </nav>
    </div>
  );
}

function NavItem({
  icon: Icon,
  label,
  href,
  active,
  badge,
}: {
  icon: typeof Home;
  label: string;
  href: string;
  active?: boolean;
  badge?: number;
}) {
  return (
    <Link
      to={href}
      className={cn(
        "relative flex flex-col items-center gap-1 rounded-md py-2 text-[10px] font-semibold transition-colors",
        active ? "bg-primary-soft text-primary" : "text-muted-foreground hover:text-foreground",
      )}
      aria-current={active ? "page" : undefined}
    >
      <div className="relative">
        <Icon className="h-4 w-4" aria-hidden />
        {badge ? (
          <span className="absolute -right-2 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-severe text-[8px] font-bold text-white">
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
      </div>
      {label}
    </Link>
  );
}
